import { useLocalSearchParams } from 'expo-router';
import * as React from 'react';
import { BodyMediumRegular, FAQAccordionScreen, YStack } from 'smoothie';

import {
    AccountVerificationLetterLink,
    useAccountVerificationLetterFlow,
} from '../../banking/components/account-verification-letter';
import { useSendEmailToSupport } from '../../common/hooks/useSendEmailToSupport';
import { useViewEvents } from '../../events/hooks/useViewEvents';
import { SUPPORT_EMAIL } from '../../settings/types/constants';
import {
    ROUTING_NUMBER_FAQ_EXPAND_PARAM,
    ROUTING_NUMBER_FAQ_QUESTION,
} from '../constants/bankingSwitchFaq';

const BankingSwitchFAQ = () => {
    const sendEmailToSupport = useSendEmailToSupport();
    const { expand } = useLocalSearchParams<{ expand?: string }>();
    const { trackViewOnce } = useViewEvents();
    trackViewOnce('BankingSwitch/FAQ');

    const { openSheet, flowElements } = useAccountVerificationLetterFlow({
        variant: 'bill-pay',
    });

    const routingNumberFaqAnswer = (
        <YStack gap={12}>
            <BodyMediumRegular color="$gray1100">
                Some companies may use outdated systems that don&apos;t
                recognize our routing number. If you get an error,{' '}
                <AccountVerificationLetterLink onPress={openSheet}>
                    submit this letter
                </AccountVerificationLetterLink>{' '}
                to your bill provider to verify your account.
            </BodyMediumRegular>
            <BodyMediumRegular color="$gray1100">
                For additional support, reach out to Fruitful Hospitality{' '}
                <BodyMediumRegular
                    color="$gray1100"
                    textDecorationLine="underline"
                    onPress={() => {
                        void sendEmailToSupport();
                    }}
                    hoverStyle={{ cursor: 'pointer' }}
                >
                    {SUPPORT_EMAIL}
                </BodyMediumRegular>
                .
            </BodyMediumRegular>
        </YStack>
    );

    const bankingSwitchFAQs = [
        {
            question:
                'How do I start using Fruitful for my bills and subscriptions?',
            answer: `Start by updating your billing information wherever you pay bills or have subscriptions. Use your Fruitful virtual bills card number wherever possible — you’ll earn cash back for eligible transactions.`,
        },
        {
            question: ROUTING_NUMBER_FAQ_QUESTION,
            answer: routingNumberFaqAnswer,
        },
        {
            question: 'Can I use Venmo with my Fruitful account?',
            answer: 'Yes, you can connect your Fruitful Cash Accounts to Venmo via Plaid or with your account and routing numbers.',
        },
    ];

    return (
        <>
            <FAQAccordionScreen
                faqs={bankingSwitchFAQs}
                title="Paying bills with Fruitful"
                description="More info and FAQ"
                fallbackRoute="/home"
                defaultOpenQuestions={
                    expand === ROUTING_NUMBER_FAQ_EXPAND_PARAM
                        ? [ROUTING_NUMBER_FAQ_QUESTION]
                        : undefined
                }
            />
            {flowElements}
        </>
    );
};

export default BankingSwitchFAQ;
